import * as Sentry from '@sentry/react'
import {getFeedback} from '@sentry/react'
import {MessageCircle} from 'lucide-react'
import {
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	useSidebar,
} from '@/components/ui/sidebar'

type FeedbackIntegration = NonNullable<ReturnType<typeof getFeedback>>

// One form instance for the app's lifetime, cached at module scope. The cache
// must NOT live in the component: on mobile this item sits inside the sidebar
// Sheet, whose subtree unmounts ~150ms after every close. The previous
// attachTo()-in-useEffect wiring died exactly that way — Sentry's detach()
// doesn't just remove the click listener, it removes an open dialog from the
// DOM, so tapping "Contact us" closed the sheet and the cleanup tore the form
// down mid-open (visible as a flash). createForm() has no such lifecycle tie.
let formPromise: ReturnType<FeedbackIntegration['createForm']> | null = null

async function openFeedbackForm(): Promise<void> {
	const feedback = getFeedback()
	if (!feedback) {
		// Expected in dev (Sentry disabled). In prod it means the browser
		// Sentry client didn't init — almost always VITE_SENTRY_DSN missing
		// at build time. Surface it so this failure isn't silent next time.
		if (import.meta.env.PROD) {
			console.warn(
				'[ContactMenuItem] Sentry feedback unavailable — is VITE_SENTRY_DSN set at build time?',
			)
		}
		return
	}
	try {
		formPromise ??= feedback.createForm()
		const form = await formPromise
		// Repeat calls on the cached form are how Sentry's own attachTo()
		// reopens its dialog — appendToDom is a no-op once attached.
		form.appendToDom()
		form.open()
	} catch (error) {
		// Lazy-loading the feedback modal chunk can fail (offline, blocked).
		// Drop the cache so the next tap retries from scratch.
		formPromise = null
		Sentry.captureException(error)
	}
}

/**
 * "Contact us" entry in the sidebar footer. Opens the Sentry User Feedback
 * form for feedback, bug reports, and general inquiries.
 *
 * Sentry is only enabled in production (see main.tsx), so getFeedback()
 * returns undefined in local dev — the button still renders but is inert
 * there. The form is opened imperatively on click (never attachTo — see the
 * cache comment above) and lives on document.body, so it survives the mobile
 * sheet closing underneath it.
 */
export function ContactMenuItem() {
	const {setOpenMobile} = useSidebar()

	return (
		<SidebarMenu>
			<SidebarMenuItem>
				<SidebarMenuButton
					tooltip='Contact us'
					onClick={() => {
						setOpenMobile(false)
						void openFeedbackForm()
					}}
				>
					<MessageCircle />
					<span>Contact us</span>
				</SidebarMenuButton>
			</SidebarMenuItem>
		</SidebarMenu>
	)
}
